// Ekran 06 — Üretim Beyanı.
// Sınıf adları ref_files/btys-erp-06-uretim-beyani.html'den birebir alındı.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { tr, trBugun, trTarih } from '../../lib/trFormat'
import { ApiHatasi } from '../../lib/api'
import { useYetki, YetkiKapisi } from '../../lib/yetki'
import BeyanGridi from './BeyanGridi'
import GunOzetiPaneli from './GunOzetiPaneli'
import KpiSeridi from './KpiSeridi'
import OlayDrawer, { TANIM } from './OlayDrawer'
import {
  beyan, type BeyanGunOzeti, type BeyanSatiri, type OlayTipi, type TesisSatiri,
} from './api'

/**
 * Ekran BUGÜNLE açılıyor (kullanıcı kararı, docs/kararlar-ek.md).
 *
 * Önceden seed'in gün hikâyesine (15.08.2026) çapalıydı: demo için doğruydu,
 * işletme için değil. Operatör günde onlarca kez bu ekrana giriyor ve her
 * seferinde gün seçicisinden bugünü bulmak zorunda kalıyordu; üstelik yanlışlıkla
 * geçmiş bir güne veri girmek mümkündü.
 *
 * Tarih Europe/Istanbul'a göre hesaplanıyor (§12) — tarayıcının saat dilimi
 * neyse olsun tesisin takvim günü çıkar. Gün kaydı henüz açılmamışsa sunucu
 * kaydedilmemiş bir taslak döndürüyor (BeyanOkuma.GunuBulAsync), yani boş grid
 * gelir ve ilk yazmada gün kendiliğinden açılır.
 */

const GUN_ADI = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']

/**
 * Gün seçicinin etiketi: "15.08 Cumartesi" (ref:383-386 kalıbı).
 *
 * Gün adı takvimden hesaplanıyor: referans HTML "15.08 Cuma" yazıyor ama
 * 15.08.2026 bir Cumartesi — elle yazılmış etiketi kopyalamak yerine doğrusu
 * gösteriliyor. Aynı yardımcı ekran 07'de de var; `trFormat` ortak kütüphanesi
 * bu tur kapsamında değiştirilmediği için kopyalandı (bkz. docs/kararlar-ek.md).
 */
function gunEtiketi(iso: string): string {
  const [y, a, g] = iso.split('-').map(Number)
  const d = new Date(Date.UTC(y, a - 1, g))
  return `${String(g).padStart(2, '0')}.${String(a).padStart(2, '0')} ${GUN_ADI[d.getUTCDay()]}`
}

function Icerik() {
  const [tarih, setTarih] = useState(trBugun)
  const [seciliUnite, setSeciliUnite] = useState('tumu')
  // useFitInsp KALDIRILDI: gün özeti sağ panelden çıkıp KPI altına yatay
  // şeride taşındı; sabit yükseklikli bir panel kalmadığı için hesaplanacak
  // maxHeight de yok.
  const { ac } = useDrawer()
  const kuyruk = useQueryClient()
  const { izinVar, onaylayabilir } = useYetki()
  const yazabilir = izinVar('Beyan', 'yaz')

  const { data: gun, isLoading } = useQuery({
    queryKey: ['beyan', tarih],
    queryFn: () => beyan.gun(tarih),
  })

  const { data: mutabakat } = useQuery({
    queryKey: ['beyan-mutabakat', tarih],
    queryFn: () => beyan.mutabakat(tarih),
  })

  // ref:383-386 gün seçicisi. Liste BUGÜNDEN geriye 7 gün; bitiş sunucuda
  // hesaplanıyor (Zaman.BugunTr) — istemcinin saatine bağlanmıyor. Seçilen
  // tarih değişse de seçenekler kaymaz.
  const { data: gunler = [] } = useQuery({
    queryKey: ['beyan-gunler'],
    queryFn: () => beyan.gunler(),
  })

  function tazele() {
    kuyruk.invalidateQueries({ queryKey: ['beyan'] })
    kuyruk.invalidateQueries({ queryKey: ['beyan-mutabakat'] })
    // Onay gün durumunu değiştiriyor; seçenek etiketi "Taslak"ta kalmasın.
    kuyruk.invalidateQueries({ queryKey: ['beyan-gunler'] })
  }

  const hata = (h: unknown, varsayilan: string) =>
    toast(h instanceof ApiHatasi ? h.message : varsayilan)

  const degerYaz = useMutation({
    mutationFn: (g: { parametreId: string; saat: number; deger: number | null }) =>
      beyan.degerler(tarih, [g]),
    onSuccess: (yeni) => kuyruk.setQueryData(['beyan', tarih], yeni),
    onError: (h) => { hata(h, 'Değer kaydedilemedi'); tazele() },
  })

  const tesisYaz = useMutation({
    mutationFn: (g: { tip: TesisSatiri['tip']; saat: number; deger: number | null }) =>
      beyan.tesis(tarih, [g]),
    onSuccess: (yeni) => kuyruk.setQueryData(['beyan', tarih], yeni),
    onError: (h) => { hata(h, 'Değer kaydedilemedi'); tazele() },
  })

  // ref:669-674 "İlk değerle satırı doldur" tek hücreyi değil satırın TÜM boş
  // hücrelerini yazıyor; uçlar zaten dizi aldığı için tek istekle gidiyor
  // (24 ayrı PUT gridi bir saniye boyunca titretirdi).
  const degerlerToplu = useMutation({
    mutationFn: (g: {
      girdiler: { parametreId: string; saat: number; deger: number | null }[]
      mesaj: string
    }) => beyan.degerler(tarih, g.girdiler),
    onSuccess: (yeni, g) => {
      kuyruk.setQueryData(['beyan', tarih], yeni)
      toast(g.mesaj)
    },
    onError: (h) => { hata(h, 'Satır doldurulamadı'); tazele() },
  })

  const tesisToplu = useMutation({
    mutationFn: (g: {
      girdiler: { tip: TesisSatiri['tip']; saat: number; deger: number | null }[]
      mesaj: string
    }) => beyan.tesis(tarih, g.girdiler),
    onSuccess: (yeni, g) => {
      kuyruk.setQueryData(['beyan', tarih], yeni)
      toast(g.mesaj)
    },
    onError: (h) => { hata(h, 'Satır doldurulamadı'); tazele() },
  })

  const hesapla = useMutation({
    mutationFn: (tip: TesisSatiri['tip']) => beyan.hesapla(tarih, tip),
    onSuccess: (yeni) => {
      kuyruk.setQueryData(['beyan', tarih], yeni)
      toast('Satır ünite verilerinden hesaplandı — üzerine yazabilirsiniz (K28)')
    },
    onError: (h) => hata(h, 'Hesaplanamadı'),
  })

  const dunuKopyala = useMutation({
    mutationFn: () => beyan.dunuKopyala(tarih),
    onSuccess: (s) => { tazele(); toast(s.mesaj) },
    onError: (h) => hata(h, 'Kopyalanamadı'),
  })

  const onayla = useMutation({
    mutationFn: () => beyan.onayla(tarih),
    onSuccess: (s) => { tazele(); toast(s.mesaj) },
    onError: (h) => {
      if (h instanceof ApiHatasi && h.kod === 'eksik_hucre') {
        toast(h.message)
        return
      }
      hata(h, 'Onaylanamadı')
    },
  })

  if (isLoading || !gun) {
    return <div className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</div>
  }

  const kilitli = gun.durum === 'onayli'

  // K30: TESİS sekmesinin pipi. Sunucu bu eksikleri "Tümü" pipine zaten
  // katıyor (BeyanOkuma), ama sekmede gösterilmeyince hangi grupta oldukları
  // görünmüyordu. Onaylı günde tüm pipler kapalı.
  const tesisPip = kilitli ? 0 : gun.tesis.reduce((a, t) => a + t.eksikSaatler.length, 0)

  // ref:556 `#cnt` — referansın ROWS süzgeci: lab ve olay satırları hariç,
  // TESİS veri satırları dahil. Ünite sayısında +1 TESİS grubu içindir.
  const satirSayisi = gun.tesis.length + gun.uniteler.reduce(
    (a, u) => a + u.satirlar.filter((r) => r.kaynak !== 'lab' && r.frek !== 'olay').length, 0)

  // Onay düğmesi başlıkta çizilir (panel altlığı kalktı); koşul ve gerekçe
  // tek yerde türetiliyor ki ikisi ayrışmasın.
  const onayDisabled = !onaylayabilir || kilitli || onayla.isPending
  // Liste yüklenene kadar select boş açılmasın diye açık gün tek seçenek olur.
  const gunSecenekleri: BeyanGunOzeti[] = gunler.length > 0
    ? gunler
    : [{ tarih, durum: gun.durum, var: true }]
  const onayEngeli = kilitli ? 'Gün zaten onaylı'
    : onaylayabilir ? undefined
      : 'K16: gün onayı için ayrıca yetkilendirilmeniz gerekir'

  /**
   * Olay drawer'ı (K23). Tek tetikleyici gridin olay satırındaki "+ kayıt"
   * düğmesi (ref:636) — tip ve ünite oradan geldiği için başlık referansın
   * dört başlığından biri olarak sabitlenebiliyor (ref:697).
   */
  function olayDrawerAc(tip: OlayTipi, uniteId: string) {
    ac({
      baslik: TANIM[tip].baslik,
      altBaslik: 'Olay bazlı beyan kaydı (K23)',
      boyut: 'sm',
      icerik: <OlayDrawer tarih={tarih} tip={tip} uniteId={uniteId} />,
    })
  }

  /**
   * ref:669-674 `rowFill` — satırın İLK dolu değerini yalnız BOŞ hücrelere
   * yazar. `null` hücre sıklığın o saatte hücre açmadığı anlamına gelir ve
   * asla doldurulmaz (grid hücre sözleşmesi).
   */
  function satirDoldur(satir: BeyanSatiri) {
    const ilk = satir.hucreler.find((h) => h && h.deger !== null)?.deger ?? null
    if (ilk === null) {
      toast('Önce satıra en az bir değer girin')
      return
    }
    const girdiler = satir.hucreler.flatMap((h, saat) =>
      h && h.deger === null ? [{ parametreId: satir.parametreId, saat, deger: ilk }] : [])
    if (girdiler.length === 0) {
      toast(`${satir.ad} satırında boş hücre yok`)
      return
    }
    degerlerToplu.mutate({
      girdiler,
      mesaj: `${satir.ad} satırı ${tr(ilk, satir.basamak)} ile dolduruldu — düzenleyebilirsiniz`,
    })
  }

  /** Aynı araç TESİS satırlarında (K28) — hepsi saatlik, basamak 0. */
  function tesisDoldur(tesis: TesisSatiri) {
    const ilk = tesis.hucreler.find((h) => h && h.deger !== null)?.deger ?? null
    if (ilk === null) {
      toast('Önce satıra en az bir değer girin')
      return
    }
    const girdiler = tesis.hucreler.flatMap((h, saat) =>
      h && h.deger === null ? [{ tip: tesis.tip, saat, deger: ilk }] : [])
    if (girdiler.length === 0) {
      toast(`${tesis.etiket} satırında boş hücre yok`)
      return
    }
    tesisToplu.mutate({
      girdiler,
      mesaj: `${tesis.etiket} satırı ${tr(ilk, 0)} ile dolduruldu — düzenleyebilirsiniz`,
    })
  }

  return (
    <>
      <div className="crumb">
        Operasyon <b>/ Üretim Beyanı</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Üretim Beyanı</h1>
        <select className="sel" value={tarih} onChange={(e) => setTarih(e.target.value)}>
          {gunSecenekleri.map((g) => (
            <option key={g.tarih} value={g.tarih}>
              {gunEtiketi(g.tarih)} — {g.durum === 'onayli' ? 'Onaylı' : 'Taslak'}
            </option>
          ))}
        </select>
        {/* Referansın #gun-durum rozeti: gün durumu ve eksik hücre sayısı KPI
            şeridinde değil burada duruyor (§3.2 · ref 553-555). */}
        {kilitli ? (
          <span className="badge b-navy">
            🔒 Onaylı — salt okunur (K15) · {gun.onayKullanici ?? '—'}
          </span>
        ) : (
          <span className="badge b-amber">Taslak · {gun.toplamPip} hücre eksik</span>
        )}
        <div className="page-actions">
          {!kilitli && (
            <button
              className="btn btn-ghost"
              type="button"
              disabled={!yazabilir || dunuKopyala.isPending}
              onClick={() => dunuKopyala.mutate()}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Dünü kopyala
            </button>
          )}
          {/* Referansta bu düğme page-actions'ta (ref 561). Ucu yok: yan etkisiz
              bir düğme sahte toast basmasın diye kapalı ve nedeni title'da. */}
          <button
            className="btn btn-ghost"
            type="button"
            disabled
            title="Dışa aktarma ucu henüz yok (Faz 7 dışı)"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Excel'e aktar
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={onayDisabled}
            title={onayEngeli}
            onClick={() => onayla.mutate()}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            {onayla.isPending ? 'Onaylanıyor…' : 'Günü topluca onayla'}
          </button>
        </div>
      </div>

      <KpiSeridi gun={gun} />

      {/*
        Gün özeti KPI'ların ALTINDA, yatay şerit (kullanıcı isteği). Önceden
        sağ inceleme panelindeydi ve tabloyu 24 saat sütunuyla birlikte dar bir
        alana sıkıştırıyordu; şerit hem özeti hem tabloyu rahatlattı.
        Onay düğmesi başlıkta zaten var, şeritte tekrarlanmıyor.
      */}
      <div className="card ozet-serit" style={{ marginBottom: 10 }}>
        <div className="insp-h">
          <div className="insp-eyebrow">
            <span className="kod">{trTarih(tarih)}</span>
            <span>·</span>
            <span>{kilitli ? 'Onaylı' : 'Taslak'}</span>
          </div>
          <div className="insp-t">Tesis Beyanı — Gün Özeti</div>
        </div>
        <GunOzetiPaneli gun={gun} mutabakat={mutabakat} />
      </div>

      <div className="list-grid">
        <div className="card">
          <div className="toolbar" style={{ rowGap: 6 }}>
            <div className="seg">
              <button
                type="button"
                className={seciliUnite === 'tumu' ? 'seg-i on' : 'seg-i'}
                onClick={() => setSeciliUnite('tumu')}
              >
                Tümü
                {gun.toplamPip > 0 && !kilitli && (
                  <span
                    className="seg-pip"
                    title={`Saat ${String(gun.sinirSaat).padStart(2, '0')}:00 öncesinde ${gun.toplamPip} eksik/eşik dışı hücre`}
                  >
                    {gun.toplamPip}
                  </span>
                )}
              </button>
              <button
                type="button"
                className={seciliUnite === 'TESIS' ? 'seg-i on' : 'seg-i'}
                onClick={() => setSeciliUnite('TESIS')}
              >
                TESİS
                {tesisPip > 0 && (
                  <span
                    className="seg-pip"
                    title={`Saat ${String(gun.sinirSaat).padStart(2, '0')}:00 öncesinde ${tesisPip} eksik/eşik dışı hücre`}
                  >
                    {tesisPip}
                  </span>
                )}
              </button>
              {gun.uniteler.map((u) => (
                <button
                  key={u.uniteId}
                  type="button"
                  className={seciliUnite === u.uniteId ? 'seg-i on' : 'seg-i'}
                  onClick={() => setSeciliUnite(u.uniteId)}
                >
                  {u.kod}
                  {u.pip > 0 && !kilitli && (
                    <span
                      className="seg-pip"
                      title={`Saat ${String(gun.sinirSaat).padStart(2, '0')}:00 öncesinde ${u.pip} eksik/eşik dışı hücre`}
                    >
                      {u.pip}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {/* ref:407-408 — renk kodu açıklaması ve satır/ünite sayacı */}
            <span className="cell-sub">
              gri — periyot dışı · mavi lab (K21) · amber hücre = geciken eksik / eşik dışı
            </span>
            <div className="toolbar-right">
              <span>{satirSayisi} parametre satırı · {gun.uniteler.length + 1} ünite</span>
            </div>
          </div>

          <BeyanGridi
            gun={gun}
            seciliUnite={seciliUnite}
            yazabilir={yazabilir}
            degerYaz={(parametreId, saat, deger) => degerYaz.mutate({ parametreId, saat, deger })}
            tesisYaz={(tip, saat, deger) => tesisYaz.mutate({ tip, saat, deger })}
            hesapla={(tip) => hesapla.mutate(tip)}
            satirDoldur={satirDoldur}
            tesisDoldur={tesisDoldur}
            olayEkle={olayDrawerAc}
          />
          {/* Referansta `.card` toolbar + bey-wrap ile biter (ref:404-411);
              `.grid-foot` bu ekranda hiç kullanılmıyor. Düşen iki bilgi zaten
              ekranda: kilit metni başlıktaki `#gun-durum` rozetinde, sıklık
              açıklaması satır etiketinin `.bpu2` rozetinde ve "—" hücrelerinde. */}
        </div>

      </div>
    </>
  )
}

export default function BeyanSayfasi() {
  return (
    <Kabuk listeBasligi="Üretim Beyanı">
      <YetkiKapisi modul="Beyan">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
